import { Fragment, type ReactNode, useState } from 'react'
import { DownOutlined, UpOutlined } from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { core } from '@musinsa/pbo-core'

export interface FilterCardField {
  key: string
  label: string
  required?: boolean
  /** 접힌 상태의 값 칩 문구. null 이면 접혔을 때 칩을 숨긴다. */
  chip: string | null
  control: ReactNode
}

interface Props {
  fields: FilterCardField[]
  onReset: () => void
}

const FILTER_DIVIDER_STYLE = { height: 18, borderLeft: '1px solid #d9d9d9', display: 'block', flex: 'none' } as const

/**
 * 일마감조회 2화면(RP12·RP13) 조회영역. GR09 입고조회 2.0 생성본의 FilterCard 구조를 따른다.
 *
 * `core.Card > div(.pbo-filter-collapse-open|closed) > [Flex(20, wrap) 필드 · Flex(8, end) 푸터]`.
 * 필드는 라벨 위 `core.FormItem`(pbo-filter-field) 이고 한 줄 3~4개 배치는 pbo-core CSS 가 폭으로 정한다.
 *
 * antd Form 안에 둔다. 접어도 필드를 언마운트하지 않고 숨기기만 한다.
 * 언마운트된 Form.Item 값은 onFinish 가 받는 값에서 빠진다.
 */
export function FilterCard({ fields, onReset }: Readonly<Props>) {
  const [collapsed, setCollapsed] = useState(false)

  return (
    <core.Card styles={{ body: { padding: '12px 20px' } }} style={{ border: '1px solid #d9d9d9', borderRadius: 8, minWidth: 0 }}>
      <div className={collapsed ? 'pbo-filter-collapse-closed' : 'pbo-filter-collapse-open'}>
        {collapsed && <span className="pbo-filter-empty-msg">필터가 적용된 항목이 없어요. 필터를 열어 원하는 조건으로 검색해 보세요.</span>}
        <core.Flex gap={20} align="flex-start" wrap>
          {fields.map((field) => (
            <Fragment key={field.key}>
              {collapsed && (
                <div
                  className="pbo-filter-closed"
                  hidden={field.chip == null}
                  role="button"
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setCollapsed(false)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    setCollapsed(false)
                  }}
                >
                  <div className="pbo-filter-chip-label">
                    <label>{field.label}</label>
                    {field.required ? <span className="pbo-filter-req">*</span> : null}
                  </div>
                  <div className="pbo-filter-chip-value">{field.chip}</div>
                </div>
              )}
              <core.FormItem
                className="pbo-filter-field"
                label={field.label}
                required={field.required}
                labelPosition="top"
                boxedItem={false}
                style={collapsed ? { display: 'none' } : undefined}
              >
                {field.control}
              </core.FormItem>
            </Fragment>
          ))}
        </core.Flex>
        <core.Flex gap={8} justify="flex-end" align="center">
          <core.Button icon={collapsed ? <DownOutlined /> : <UpOutlined />} onClick={() => setCollapsed((current) => !current)}>
            {`필터 ${collapsed ? '열기' : '닫기'}`}
          </core.Button>
          <span style={FILTER_DIVIDER_STYLE} />
          <core.Button onClick={onReset}>초기화</core.Button>
          <core.Button type="primary" htmlType="submit">
            조회
          </core.Button>
        </core.Flex>
      </div>
    </core.Card>
  )
}

export const chipSelect = (options: readonly { value: string; label: string }[], value: unknown): string | null =>
  value == null || value === '' ? null : (options.find((option) => option.value === value)?.label ?? String(value))

export const chipText = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value.trim() : null)

export const chipCodes = (value: unknown): string | null =>
  Array.isArray(value) && value.length ? `${String(value[0])}${value.length > 1 ? ` 외 ${value.length - 1}개` : ''}` : null

export const chipRange = (value: unknown): string | null => {
  if (!Array.isArray(value)) return null
  const [from, to] = value as [Dayjs | null | undefined, Dayjs | null | undefined]
  return from && to ? `${from.format('YY-MM-DD')} ~ ${to.format('YY-MM-DD')}` : null
}
